import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { Model, ReasoningEffort } from '../types';

import { ConversationTool } from './ConversationTool';
import type { Tool } from './Tool';

import { ApiService } from 'js/src/client/services.gen';
import type { TemplateInfoItem, TemplateInfoResponse } from 'js/src/client/types.gen';

export const findTemplatesSchema = z.object({
    search_term: z
        .string()
        .describe(
            'Search term describing the desired template — can include industry, use case, colour, style, or other characteristics (e.g., "healthcare explainer", "bold red promo", "corporate professional").',
        ),
    aspect_ratio: z
        .enum(['landscape', 'square', 'vertical', 'fourfive'])
        .optional()
        .describe('Optional filter by aspect ratio.'),
});
export type FindTemplatesArgs = z.infer<typeof findTemplatesSchema>;

export class FindTemplatesTool extends ConversationTool<typeof findTemplatesSchema> {
    protected override readonly schema = findTemplatesSchema;
    protected override readonly toolName = TOOL_NAMES.findTemplates;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Search for templates using a free-text search term. Returns the best matching templates.';

    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;

    private maxIterations = 1;

    public override getToolDisplayName(): string {
        return 'Searching templates';
    }

    protected getTools(): Tool[] {
        return [];
    }

    private createSystemMessage(templates: TemplateInfoItem[]): string {
        const templateList = templates
            .map((template, index) => {
                const parts = [`${index + 1}. Template ID: "${template.id}"`];
                parts.push(`   Aspect Ratios: ${template.aspect_ratio.join(', ')}`);
                if (template.description) {
                    parts.push(`   Description: ${template.description}`);
                }
                return parts.join('\n');
            })
            .join('\n\n');

        return `You are a template recommendation specialist that helps users find the best templates for their needs.

## Your Mission
Analyze the user's search term and select the best matching templates from the available options.

## Available Templates
${templateList}

## Instructions
1. Carefully read the user's search term — it may describe industry, use case, colour, style, or other characteristics
2. Analyze each available template's characteristics and descriptions
3. Select the templates that are a good match, ordered from best match to worst
4. Only include templates that genuinely match — return an empty array if nothing matches

## Response Format
You must respond with ONLY a JSON array of template ID strings, nothing else.

Example: ["example-template-id-1", "example-template-id-2"]`;
    }

    public get statusIcon(): string {
        return 'search';
    }

    /**
     * Extracts the JSON array of template IDs from the LLM response and maps it
     * to the known templates (preserving the LLM's ranking). Returns null when
     * the response contains no parsable JSON array.
     */
    private parseMatchingTemplates(
        responseText: string,
        templates: TemplateInfoItem[],
    ): TemplateInfoItem[] | null {
        const start = responseText.indexOf('[');
        const end = responseText.lastIndexOf(']');
        if (start === -1 || end <= start) {
            return null;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(responseText.slice(start, end + 1));
        } catch {
            return null;
        }

        if (!Array.isArray(parsed)) {
            return null;
        }

        const templatesById = new Map(templates.map((template) => [template.id, template]));
        const matched: TemplateInfoItem[] = [];
        for (const id of parsed) {
            const template = typeof id === 'string' ? templatesById.get(id) : undefined;
            if (template && !matched.includes(template)) {
                matched.push(template);
            }
        }

        // A non-empty array whose IDs we can't map to any known template means
        // we couldn't make sense of the response — treat it as unparsable.
        if (parsed.length > 0 && matched.length === 0) {
            return null;
        }

        return matched;
    }

    private formatMatchingTemplates(templates: TemplateInfoItem[], searchTerm: string): string {
        const templateList = templates
            .map((template, index) => {
                const parts = [`${index + 1}. ${template.id}`];
                if (template.description) {
                    parts.push(`   Description: ${template.description}`);
                }
                return parts.join('\n');
            })
            .join('\n');

        return `Found ${templates.length} matching template${templates.length === 1 ? '' : 's'} for "${searchTerm}" (best match first):\n\n${templateList}`;
    }

    public async execute(
        args: FindTemplatesArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { search_term, aspect_ratio } = args;

        try {
            // Fetch template information from API
            const response = await ApiService.templateInfoList();
            if (!response.data) {
                return {
                    result: 'Failed to fetch template information from the server.',
                    isSuccess: false,
                };
            }

            // The API returns TemplateInfoResponse directly (not paginated)
            // Type assertion needed due to mismatch in generated types
            const templateData = response.data as unknown as TemplateInfoResponse;
            const { custom_templates, public_templates } = templateData;

            // Combine all templates
            const allTemplates: TemplateInfoItem[] = [...custom_templates, ...public_templates];

            if (allTemplates.length === 0) {
                return {
                    result: 'No templates available.',
                    isSuccess: false,
                };
            }

            // Filter by aspect ratio if specified
            let filteredTemplates = allTemplates;
            if (aspect_ratio) {
                filteredTemplates = allTemplates.filter((template) =>
                    template.aspect_ratio.includes(aspect_ratio),
                );

                if (filteredTemplates.length === 0) {
                    return {
                        result: `No templates found that support ${aspect_ratio} aspect ratio.`,
                        isSuccess: false,
                    };
                }
            }

            const conversation = this.createChildConversationForToolCall(context.toolCallId, {
                systemMessage: this.createSystemMessage(filteredTemplates),
                tools: this.getTools(),
                maxIterations: this.maxIterations,
                task: 'find_templates_tool',
                requiresVideoState: false,
            });

            // Add user message with the search term
            conversation.addUserMessage([
                {
                    type: 'text',
                    text: search_term,
                },
            ]);

            // Execute the conversation to get the LLM's recommendations
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });

            if (!result.success) {
                return {
                    result: `Template search failed: ${result.message || 'Unknown error'}`,
                    isSuccess: false,
                };
            }

            const responseText = result.message || '';
            const matchedTemplates = this.parseMatchingTemplates(responseText, filteredTemplates);

            // Fall back to passing the LLM response through when it isn't parsable
            if (matchedTemplates === null) {
                return {
                    result: responseText,
                    isSuccess: true,
                };
            }

            if (matchedTemplates.length === 0) {
                return {
                    result: `No matching templates found for "${search_term}". Try a broader search term.`,
                    isSuccess: true,
                };
            }

            return {
                result: this.formatMatchingTemplates(matchedTemplates, search_term),
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return {
                result: `Error searching for templates: ${errorMessage}`,
                isSuccess: false,
            };
        }
    }
}
